import React from 'react';
import { Loader2, MapPin, Users, CheckCircle, TrendingUp } from 'lucide-react';

import { useTrendingEvents } from '../hooks/useTrendingEvents';
import { useAuth } from '../contexts/AuthContext';
import { useEventImage } from '../hooks/useEventThumbnail';

import { goToCalendar, goToGallery, goToEventDetail, goToLogin } from '../utils/navigation';
import AdSense from './AdSense';

const FeaturedSections: React.FC = () => {

  
  // Fetch trending events
  const { events: trendingEvents, loading: trendingLoading } = useTrendingEvents({
    limit: 6,
    sortBy: 'START_DATE',
    sortOrder: 'DESC'
  });
  
  const { user } = useAuth();


  // Component for rendering event image with thumbnail API support
  const EventImage: React.FC<{ event: any }> = ({ event }) => {
    const eventId = event.id || event.EVENT_ID;
    const { thumbnailUrl, loading } = useEventImage(event, eventId);
    
    if (loading) {
      return (
        <div className="w-full h-72 bg-gray-200 animate-pulse flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      );
    }
    
    return (
      <img
        src={thumbnailUrl || 'https://images.pexels.com/photos/6113659/pexels-photo-6113659.jpeg?auto=compress&cs=tinysrgb&w=300'}
        alt={event.title || event.TITLE || 'Event'}
        className="w-full h-72 object-contain"
        onError={(e) => {
          console.error('❌ Image failed to load for event:', event.title || event.TITLE);
          e.currentTarget.src = 'https://images.pexels.com/photos/6113659/pexels-photo-6113659.jpeg?auto=compress&cs=tinysrgb&w=300';
        }}
      />
    );
  };





  // Handler for opening event details
  const handleViewEventDetails = (puja: any) => {
    // Check if user is authenticated
    if (!user) {
      // User is not logged in, redirect to login page
      goToLogin();
      return;
    }

    // User is authenticated, navigate to event detail page
    const eventId = puja.id || puja.originalEvent?.id || puja.originalEvent?.EVENT_ID;
    const eventTitle = puja.name || puja.originalEvent?.title || puja.originalEvent?.TITLE || 'Durga Puja Event';
    const eventLocation = puja.location || puja.originalEvent?.location || puja.originalEvent?.LOCATION || 'Location TBA';
    
    if (eventId) {
      goToEventDetail(eventId, eventTitle, eventLocation);
    }
  };





  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Trending Events Section */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h2 className="font-runtime text-3xl md:text-4xl font-bold text-black mb-4 flex items-center justify-center">
              <TrendingUp className="h-8 w-8 md:h-10 md:w-10 text-dpn-pink mr-3" />
              Trending Durga Puja Events
            </h2>
            <p className="font-lucy-rose text-lg text-gray-600 max-w-3xl mx-auto">
              Join the most popular and highly anticipated Durga Puja celebrations happening around the world.
            </p>
          </div>

          {trendingLoading ? (
            <div className="text-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-dpn-pink mx-auto mb-4" />
              <p className="font-lucy-rose text-gray-600">Loading trending events...</p>
            </div>
          ) : trendingEvents.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
              {trendingEvents.slice(0, 6).map((event, index) => {
                const eventDisplay = {
                  id: event.id || event.EVENT_ID || Math.random(),
                  name: event.title || event.TITLE || 'Untitled Event',
                  location: event.location || event.LOCATION || event.ADDRESS || event.address || 'Location TBA',
                  votes: event.expectedAttendance || event.EXPECTED_ATTENDANCE || 0,
                  originalEvent: event
                };

                return (
                  <div
                    key={eventDisplay.id}
                    className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 cursor-pointer group border border-gray-100 overflow-hidden flex flex-col h-full"
                    onClick={() => handleViewEventDetails(eventDisplay)}
                  >
                    {/* Trending Badge */}
                    <div className="relative">
                      <EventImage event={event} />
                      <div className="absolute top-3 left-3 bg-gradient-to-r from-orange-500 to-red-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg flex items-center">
                        <TrendingUp className="h-3 w-3 mr-1" />
                        #{index + 1} Trending
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </div>

                    <div className="p-6 flex flex-col flex-1">
                      <h3 className="font-runtime text-xl font-bold text-gray-900 mb-2 group-hover:text-dpn-pink transition-colors line-clamp-2">
                        {eventDisplay.name}
                      </h3>
                      
                      <div className="flex items-center text-gray-600 mb-3">
                        <MapPin className="h-4 w-4 mr-2 text-dpn-light-green" />
                        <span className="font-lucy-rose text-sm truncate">{eventDisplay.location}</span>
                      </div>

                      <div className="flex items-center justify-between mb-4 flex-1">
                        <div className="flex items-center text-gray-600">
                          <Users className="h-4 w-4 mr-2 text-dpn-olive" />
                          <span className="font-lucy-rose text-sm font-medium">
                            {eventDisplay.votes > 0 ? `${eventDisplay.votes.toLocaleString()}+ expected` : 'Open to all'}
                          </span>
                        </div>
                        {(event.isVerified || event.IS_VERIFIED) && (
                          <div className="flex items-center text-green-600">
                            <CheckCircle className="h-4 w-4 mr-1" />
                            <span className="text-xs font-medium">Verified</span>
                          </div>
                        )}
                      </div>

                      <div className="flex items-center justify-center mt-auto">
                        <button className="w-full bg-gradient-to-r from-dpn-pink to-dpn-raspberry hover:from-dpn-raspberry hover:to-dpn-pink text-white py-2.5 px-4 rounded-lg transition-all duration-200 text-sm font-lucy-rose font-medium transform hover:scale-105 shadow-md">
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-2xl">
              <TrendingUp className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="font-runtime text-xl font-semibold text-gray-700 mb-2">No Trending Events Yet</h3>
              <p className="font-lucy-rose text-gray-600">
                Check back soon for the most popular Durga Puja celebrations.
              </p>
            </div>
          )}
        </div>

        {/* Advertisement Section */}
        <section className="py-8 mb-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-center">
              <AdSense 
                adSlot="1234567895" 
                adFormat="auto"
                className="w-full max-w-4xl"
                adStyle={{ display: 'block', width: '970px', height: '250px' }}
              />
            </div>
          </div>
        </section>

        {/* Feature Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Puja Calendar */}
          <div className="bg-gradient-to-br from-dpn-light-green to-dpn-olive p-8 rounded-2xl text-white cursor-pointer hover:scale-105 transition-transform duration-300" onClick={goToCalendar}>
            <div className="h-12 w-12 mb-4 text-4xl">📅</div>
            <h3 className="font-runtime text-2xl font-bold mb-3">Puja Calendar 2025</h3>
            <p className="font-lucy-rose mb-6 opacity-90">
              Plan your celebrations with our comprehensive calendar of events worldwide
            </p>
            <div className="bg-white/20 hover:bg-white/30 text-white font-lucy-rose font-medium px-6 py-2 rounded-lg inline-block transition-colors duration-200">
              View Calendar
            </div>
          </div>

          {/* Gallery */}
          <div id="gallery" className="bg-gradient-to-br from-dpn-raspberry to-dpn-pink p-8 rounded-2xl text-white cursor-pointer hover:scale-105 transition-transform duration-300" onClick={goToGallery}>
            <div className="h-12 w-12 mb-4 text-4xl">📷</div>
            <h3 className="font-runtime text-2xl font-bold mb-3">Cultural Gallery</h3>
            <p className="font-lucy-rose mb-6 opacity-90">
              Explore stunning photographs and artwork from celebrations across the globe
            </p>
            <div className="bg-white/20 hover:bg-white/30 text-white font-lucy-rose font-medium px-6 py-2 rounded-lg inline-block transition-colors duration-200">
              View Gallery
            </div>
          </div>

          {/* Vote Section */}
          <div id="vote" className="bg-gradient-to-br from-gray-800 to-black p-8 rounded-2xl text-white" style={{ scrollMarginTop: '120px' }}>
            <div className="h-12 w-12 mb-4 text-4xl">🏆</div>
            <h3 className="font-runtime text-2xl font-bold mb-3">Vote for Your Favorite</h3>
            <p className="font-lucy-rose mb-6 opacity-90">
              Support your favorite Puja committees and help recognize excellence
            </p>
            <div className="bg-white/20 text-white font-lucy-rose font-medium px-6 py-2 rounded-lg inline-block">
              Coming Soon
            </div>
          </div>

          {/* Photography Contest */}
          <div className="bg-gradient-to-br from-purple-600 to-purple-800 p-8 rounded-2xl text-white md:col-span-2">
            <div className="h-12 w-12 mb-4 text-4xl">📸</div>
            <h3 className="font-runtime text-2xl font-bold mb-3">Best Photography Contest 2025</h3>
            <p className="font-lucy-rose mb-6 opacity-90">
              Capture the essence of Durga Puja and win exciting prizes. Submit your best shots from this year's celebrations.
            </p>
            <div className="bg-white/20 text-white font-lucy-rose font-medium px-6 py-2 rounded-lg inline-block">
              Coming Soon
            </div>
          </div>

          {/* Donate Section */}
          <div className="bg-gradient-to-br from-dpn-light-green to-emerald-600 p-8 rounded-2xl text-white">
            <div className="h-12 w-12 mb-4 text-4xl">💰</div>
            <h3 className="font-runtime text-2xl font-bold mb-3">Support a Committee</h3>
            <p className="font-lucy-rose mb-6 opacity-90">
              Help local committees organize beautiful celebrations in your community
            </p>
            <div className="bg-white/20 text-white font-lucy-rose font-medium px-6 py-2 rounded-lg inline-block">
              Coming Soon
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturedSections;